import Vue from 'vue'
import { useQueryClient, useMutation } from '@tanstack/vue-query'
import pluralize from 'pluralize'

import type {
  Nullable,
  Pagination,
  Paginator,
  Activity,
  ActivityId,
} from '@/types'
import { getCurrentInstance, mountVueToBody } from '@/util'
import { useActivityService, useLoading, useConfirm, usePrompt } from '@/use'

export default function useActivity() {
  const queryClient = useQueryClient()
  const instance = getCurrentInstance()
  const { service } = useActivityService()
  const { setLoading } = useLoading()
  const { confirm } = useConfirm()
  const { prompt } = usePrompt()

  async function create(): Promise<Activity | null> {
    const { default: options } = await import(
      '@/components/ActivityForm/CreateActivityForm/CreateActivityFormDialog.vue'
    )
    return new Promise((resolve) => {
      const c = new Vue({
        extends: options,
        parent: instance.proxy.$root,
      })
      const destroy = mountVueToBody(c)
      c.$once('success', (activity: Activity) => {
        resolve(activity)
        destroy()
      })
      c.$once('fail', () => {
        resolve(null)
        destroy()
      })
    })
  }

  async function edit(
    id: ActivityId,
    paginator: Nullable<Paginator> = null
  ): Promise<Nullable<Activity>> {
    setLoading(true)
    try {
      const { prev, next } = paginator ?? { prev: null, next: null }
      const pagination: Nullable<Pagination> =
        prev !== null || next !== null
          ? { hasPrev: prev !== null, hasNext: next !== null }
          : null
      const queryKey = ['activity', id]
      const activity = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => service.get(id, { relations: true }),
      })
      const { default: options } = await import(
        '@/components/ActivityForm/EditActivityForm/EditActivityFormDialog.vue'
      )
      return new Promise((resolve) => {
        const c = new Vue({
          extends: options,
          parent: instance.proxy.$root,
          propsData: { activity, pagination },
        })
        const destroy = mountVueToBody(c)
        c.$once('success', (activity: Activity) => {
          queryClient.setQueryData(queryKey, activity)
          resolve(activity)
          destroy()
        })
        c.$once('fail', () => {
          resolve(null)
          destroy()
        })

        if (prev !== null) {
          c.$once('prev', () => {
            prev()
            resolve(null)
            destroy()
          })
        }

        if (next !== null) {
          c.$once('next', () => {
            next()
            resolve(null)
            destroy()
          })
        }
      })
    } catch (e) {
      window.alert(e)
      return null
    } finally {
      setLoading(false)
    }
  }

  async function del(id: ActivityId): Promise<ActivityId | null> {
    const confirmed = await confirm('Are you sure?')
    if (confirmed === false) {
      return null
    }
    return service.delete(id)
  }

  const { mutateAsync: delManyMutateAsync } = useMutation({
    mutationFn: ({ ids }: { ids: ActivityId[] }) => service.deleteMany(ids),
  })

  async function delMany(ids: ActivityId[]): Promise<ActivityId[] | null> {
    const confirmed = await confirm(
      `Are you sure you want to delete ${ids.length} ${pluralize(
        'activity',
        ids.length
      )}`
    )
    if (confirmed === false) return null
    return delManyMutateAsync({ ids })
  }

  const { mutateAsync: copyMutateAsync } = useMutation({
    mutationFn: ({ id, name }: { id: ActivityId; name: string }) =>
      service.copy(id, name),
  })

  async function copy(
    id: ActivityId,
    initial: string
  ): Promise<Activity | null> {
    const name = await prompt({
      title: 'New Activity name',
      label: 'Name',
      initial: `${initial} [COPY]`,
    })
    if (name === undefined) {
      return null
    }
    setLoading(true)
    try {
      const activity = await copyMutateAsync({ id, name })
      return activity
    } finally {
      setLoading(false)
    }
  }

  const { mutateAsync: copyManyMutateAsync } = useMutation({
    mutationFn: ({ ids }: { ids: ActivityId[] }) => service.copyMany(ids),
  })

  async function copyMany(ids: ActivityId[]): Promise<ActivityId[]> {
    const result = await copyManyMutateAsync({ ids })
    return result
  }

  return {
    create,
    edit,
    del,
    delMany,
    copy,
    copyMany,
  }
}
